import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import { Link } from "wouter";

import NoPermissions from "../authorization/NoPermissions";
import BpCuTableEmployee from "./bp_cu_table_employee";

import { getCuYears, getCuByYear, deleteCu } from "../services/api";

function isAbortError(err) {
  if (!err) return false;
  return (
    err?.name === "AbortError" ||
    err?.name === "CanceledError" ||
    err?.code === "ERR_CANCELED" ||
    (typeof err?.message === "string" &&
      err.message.toLowerCase().includes("canceled"))
  );
}

export default function CuTabEmployee({
  token,
  employee,
  profile,
  onDelete,
  onPreview,
}) {
  const employeeId = employee?.id;

  const hasPermission =
    profile?.customer_type !== "employee" ||
    profile?.employee?.permissions.includes("hr.view_docs_ou") ||
    profile?.employee?.permissions.includes("hr.view_docs_all");

  const [yearsList, setYearsList] = useState([]);
  const [yearDetails, setYearDetails] = useState({});

  const [openYear, setOpenYear] = useState(null);
  const [loadingYears, setLoadingYears] = useState(false);

  // controller per evitare race/abort su loadYear
  const yearCtrlRef = useRef(null);

  useEffect(() => {
    if (!token || !employeeId || !hasPermission) return;

    const controller = new AbortController();
    const { signal } = controller;

    setLoadingYears(true);

    (async () => {
      try {
        const anniRaw = await getCuYears(
          token,
          signal,
          "certification_of_incomes",
          employeeId
        );

        const ordered = Array.isArray(anniRaw)
          ? [...anniRaw].sort((a, b) => b.year - a.year)
          : [];

        setYearsList(ordered);
      } catch (err) {
        if (isAbortError(err)) return;

        console.error("Errore caricamento anni CU:", err);
        Swal.fire({
          icon: "error",
          title: "Errore",
          text: "Errore nel caricamento degli anni CU",
        });
      } finally {
        setLoadingYears(false);
      }
    })();

    return () => controller.abort();
  }, [token, employeeId, hasPermission]);

  async function loadYearIfNeeded(year) {
    // se già caricato e non in loading, non rifare
    if (yearDetails[year]?.allDocs && !yearDetails[year]?.loading) {
      return;
    }

    // abort richiesta precedente (se clic rapido su più anni)
    if (yearCtrlRef.current) {
      yearCtrlRef.current.abort();
      yearCtrlRef.current = null;
    }
    const controller = new AbortController();
    yearCtrlRef.current = controller;

    setYearDetails((prev) => ({
      ...prev,
      [year]: { loading: true, allDocs: prev[year]?.allDocs || [] },
    }));

    try {
      const resp = await getCuByYear(
        token,
        year,
        controller.signal, // ✅ non null
        "certification_of_incomes",
        employeeId
      );

      // ✅ supporta sia array che paginato
      const list = Array.isArray(resp) ? resp : resp?.results || [];

      const cuDocsMapped = list.map((c) => ({
        id: c.id,
        title: `CU ${c.year ?? c.anno ?? ""}`.trim(),
        createdAt: c.created_at,
        isRead: !!c.read_at,
        readAt: c.read_at,
        download: c.download_link,
        download_link: c.download_link,
        description: c.description || "",
        year: c.year ?? c.anno,
        isConfirmed: !!c.is_confirmed,
      }));

      cuDocsMapped.sort((a, b) => {
        if ((b.year ?? 0) !== (a.year ?? 0)) return (b.year ?? 0) - (a.year ?? 0);
        return (b.id ?? 0) - (a.id ?? 0);
      });

      setYearDetails((prev) => ({
        ...prev,
        [year]: { loading: false, allDocs: cuDocsMapped },
      }));
    } catch (err) {
      if (!isAbortError(err)) {
        console.error("Errore caricamento CU anno:", year, err);
        Swal.fire({
          icon: "error",
          title: "Errore",
          text: `Errore nel caricamento delle CU ${year}`,
        });
      }

      setYearDetails((prev) => ({
        ...prev,
        [year]: { loading: false, allDocs: prev[year]?.allDocs || [] },
      }));
    } finally {
      if (yearCtrlRef.current === controller) {
        yearCtrlRef.current = null;
      }
    }
  }

  // cancellazione CU
  async function handleDeleteCuLocal(docId, year) {
    const { isConfirmed } = await Swal.fire({
      title: "Sei sicuro di voler cancellare questo file?",
      text: "L’operazione non è reversibile.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sì, elimina",
      cancelButtonText: "Annulla",
    });

    if (!isConfirmed) return;

    try {
      await deleteCu(token, docId, "certification_of_incomes");

      setYearDetails((prev) => {
        const yearData = prev?.[year];
        if (!yearData) return prev;

        const updatedDocs = (yearData.allDocs || []).filter((d) => d.id !== docId);
        
        setYearsList((yl) =>
          (Array.isArray(yl) ? yl : [])
            .map((yy) => {
              if (yy.year !== year) return yy;
              const newCount = Math.max(0, Number(yy.count || 0) - 1);
              return { ...yy, count: newCount };
            })
            .filter((yy) => yy.year !== year || Number(yy.count || 0) > 0)
        );
        
        if (updatedDocs.length === 0) {
          const copy = { ...prev };
          delete copy[year];
          setOpenYear((prevOpen) => (prevOpen === year ? null : prevOpen));
          return copy;
        }

        return {
          ...prev,
          [year]: {
            ...yearData,
            allDocs: updatedDocs,
          },
        };
      });

      Swal.fire("Eliminato!", "", "success");
    } catch (err) {
      Swal.fire("Errore", err?.message || "Impossibile eliminare.", "error");
    }
  }

  if (profile?.customer_type === "employee" && !hasPermission) {
    return <NoPermissions />;
  }

  return (
    <div className="pt-2 pb-4">
      <Link
        to={`/dipendenti/${employee.cf}/upload_cu`}
        className="relative inline-flex items-center rounded-sm bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
      >
        Carica Nuova
      </Link>

      <div className="mt-4">
        {loadingYears ? (
          <div className="text-sm italic text-gray-500 py-6 text-center">
            Caricamento...
          </div>
        ) : (
          <div className="divide-y divide-gray-200 border border-gray-200 rounded-md">
            {yearsList.length === 0 && (
              <div className="text-center text-sm text-gray-500 py-6 italic">
                Nessuna CU caricata
              </div>
            )}

            {yearsList.map((y) => {
              const isOpen = openYear === y.year;
              const detail = yearDetails[y.year] || {
                loading: false,
                allDocs: [],
              };

              return (
                <div key={y.year}>
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-4 py-3 text-left bg-white hover:bg-indigo-50"
                    onClick={async () => {
                      const nextOpen = openYear === y.year ? null : y.year;
                      setOpenYear(nextOpen);

                      if (nextOpen) {
                        await loadYearIfNeeded(y.year);
                      }
                    }}
                  >
                    <span className="text-base font-semibold text-gray-900">
                      {y.year}
                    </span>

                    <span className="text-xs text-gray-500">
                      {y.count} certificazion{y.count === 1 ? "e" : "i"}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="bg-indigo-50 px-4 py-3 overflow-x-auto">
                      {detail.loading ? (
                        <div className="text-sm italic text-gray-500 py-6 text-center">
                          Caricamento...
                        </div>
                      ) : (
                        <BpCuTableEmployee
                          type="cu"
                          documents={detail.allDocs}
                          onDelete={(docId) => handleDeleteCuLocal(docId, y.year)}
                          onPreview={onPreview}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}